import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';

import { PaymentClient } from '../backend-client/paymentBackend';

const getTermsUrl = (baseTermsUrl: string, applicationGroupId: string) => {
  const normalizedBaseUrl = baseTermsUrl.replace(/\/+$/, '');
  return `${normalizedBaseUrl}/terms/${applicationGroupId}`;
};

/**
 * Build the T&C (terms) URL. The path carries the `applicationGroupId` so a
 * single link covers every LOB in the group. The payment-frontend version is
 * taken from the `versionData` already resolved on the Quote page — no extra
 * version lookup is needed.
 */
export function getVersionedTermsUrl(
  applicationGroupId: string,
  versionData: WorkflowVersion
): string {
  const baseUrl = PaymentClient.getPaymentFrontendURL(
    versionData.paymentFrontendVersion
  );
  return getTermsUrl(baseUrl, applicationGroupId);
}

const getPaymentUrl = (basePaymentUrl: string, applicationGroupId: string) => {
  const normalizedBaseUrl = basePaymentUrl.replace(/\/+$/, '');
  return `${normalizedBaseUrl}/payment/${applicationGroupId}`;
};

/**
 * Build the payment URL. The path carries the `applicationGroupId` so it
 * covers the whole group; the payment backend accepts a group id wherever it
 * accepts an invoice id. Uses the already-resolved `versionData`, like the
 * terms URL.
 */
export function getVersionedPaymentUrl(
  applicationGroupId: string,
  versionData: WorkflowVersion
): string {
  const baseUrl = PaymentClient.getPaymentFrontendURL(
    versionData.paymentFrontendVersion
  );
  return getPaymentUrl(baseUrl, applicationGroupId);
}

const getCompleteUrl = (
  baseCompleteUrl: string,
  applicationGroupId: string
) => {
  const normalizedBaseUrl = baseCompleteUrl.replace(/\/+$/, '');
  return `${normalizedBaseUrl}/complete/${applicationGroupId}`;
};

/**
 * Build the payment-complete URL. The path carries the `applicationGroupId`
 * (like the terms URL) so it covers the whole group; the payment backend
 * resolves the group id the same way it resolves an invoice id. The
 * `applicationId` is used only to resolve the payment-frontend version.
 */
export async function getVersionedCompleteUrl(
  applicationId: string,
  applicationGroupId: string,
  versionControllerClient: VersionControllerClient
): Promise<string> {
  const version = await versionControllerClient.getVersionFromApplication(
    applicationId
  );
  const baseUrl = PaymentClient.getPaymentFrontendURL(
    version.paymentFrontendVersion
  );
  return getCompleteUrl(baseUrl, applicationGroupId);
}
